import { useContext, useRef } from 'react';
import axios, { AxiosProgressEvent, Cancel } from 'axios';
import Cookies from 'js-cookie';
import { ConsoleLog, Notification } from '@inntechcorp/it-design';

import WebContext from 'Context/WebContext';
import i18next from 'i18next';
import { APIPredefinedCodeValues } from 'Types/enums';

export type ErrorDetailProps = {
  detail    : string,
  message   : string,
  source    : string,
  type      : string
}

/**
 * 
 * usePostFetch
 * --
 * 
 * All API routes within the application pass through this class. It filters all 
 * responses returned from the API and processes them to their expected state.
 * Then, it returns the success or failure status.
 * 
 * Additionally, it retrieves and adds additional information such as language,
 * token, currency, etc., from the user's cookie to API calls.
 * 
 * @param changeLogout      The function used to log out the user when called from places where 
 *                          context data such as React.Portal cannot be accessed needs to be passed.
 * @param isUserLoggedOut   User's logout information.
 * 
 * @returns 
 * 
 */
export const usePostFetch = (changeLogout?:(status:string) => void, isUserLoggedOut?:boolean) => {

  const axiosSource: any    = useRef(null);
  const { addNotification } = Notification.useNotifications();
  
  const { ChangeLogout, isLoggingOut } = useContext(WebContext);

  const language = Cookies.get("web-language");
  //const currency = Cookies.get("web-currency");

  const translateCell = (cell:string) => {
    return (i18next.t(cell));
  }

  const logout = (status:string) => {
    
    /**
     * Wherever it can access the context information, it first invokes the function
     * to log the user out directly using the context data.
     */
    if ( ChangeLogout && !isLoggingOut ) {
      ChangeLogout(status);
    }

    /**
     * If it cannot access the context information, it uses the parameters passed
     * to the class and the logout function to ensure the user is logged out.
     */
    if ( changeLogout && !isUserLoggedOut ) {
      changeLogout(status);
    }

  }

  const addNotificationError = (message = "") => {
    addNotification({
      title: translateCell("notifications.error"),
      description: translateCell(message),
      type: "error"
    });
  };

  /**
   * Used to check if error returned in response is a cancel token error.
   * 
   * When a component unmounts, we need to cancel any potentially
   * ongoing Axios calls that result in a state update on success / fail.
   * This function sets up the appropriate useEffect to handle the cancelling.
   * 
   */
  const cancel = () => {
    if (axiosSource.current) {
      axiosSource.current.cancel("manually canceled!");
    }
  }

  /**
   * Used to generate the cancel token sent in the Axios request.
   */
  const createNewToken = () => {

    axiosSource.current = axios.CancelToken.source();
    return axiosSource.current.token;

  };

  const fetch = async (
    type          = "post",
    _url          = "",
    _params       = {}, 
    { 
      cancelable  = true, 
      onFetched   = (data:{}) => {data}, 
      onError     = (detail?:ErrorDetailProps) => {detail}, 
      onCanceled  = (message:Cancel) => {message},
      onProgress  = (event:AxiosProgressEvent) => {event}
    }, 
    _queries:any = {},
    _headers:any = {}
  ) => {

    let queries = "?lang=" + ((language === undefined) ? "not_found" : language);
        //queries += "&currency=" + ((currency === undefined) ? "not_found" : currency);

    Object.keys(_queries).forEach((property) => {
      queries += "&" + property + "=" + _queries[property];
    });

    const headers:any = {
      'Content-Type'                  : type === "put" ? 'multipart/form-data' : 'application/json',
      'Access-Control-Allow-Headers'  : 'Origin, X-Requested-With, Content-Type, Accept',
      ..._headers
    }

    if ( Cookies.get("web-token") !== "" && Cookies.get("web-token") !== undefined ) {
      headers['x-auth-token'] = Cookies.get("web-token");
    }

    if ( !_headers.hasOwnProperty("x-platform") && !_headers.hasOwnProperty("X-Platform") ) {
      if ( Cookies.get("web-platform") !== "" && Cookies.get("web-platform") !== undefined ) {
        headers['x-platform'] = Cookies.get("web-platform");
      }
    }

    let fetchType:any = axios.post;

    if ( type === "post" )  fetchType = axios.post;
    if ( type === "get" )   fetchType = axios.get;
    if ( type === "put" )   fetchType = axios.put;
    if ( type === "PATCH" ) fetchType = axios.patch;

    const config = { 
      headers,
      cancelToken       : cancelable ? createNewToken() : undefined,
      onUploadProgress  : (progressEvent:AxiosProgressEvent) => {
        onProgress(progressEvent)
      }
    }

    await fetchType(
        _url + queries, 
        type === "get" ? config : _params,
        config
      )
      .then((response:any) => {
        
        /*
        if ( response.hasOwnProperty("error") ) {
          addNotificationError(response.error.message);
          logout("error");
          if ( onError ) onError();
          return;
        }
        */

        if ( response.hasOwnProperty("data") ) {

          if ( response.data.hasOwnProperty("warnings") ) {

            if ( response.data.warnings[0].code === "tokenIsMissing" ) {
              
              if ( onError ) {

                onError({
                  type      : "TOKEN_IS_MISSING",
                  message   : translateCell("notifications.tokenIsMissing"),
                  source    : "WARNING",
                  detail    : _url + queries
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", translateCell("notifications.tokenIsMissing"), "error");
                ConsoleLog.double("🔴 API Path Details [usePostFetch]", _url + queries, "error");
                addNotificationError("notifications.tokenIsMissing");
              }
              
              logout("tokenIsMissing");
              return;

            }
            else if ( response.data.warnings[0].code === "tokenIsExpired" ) {
              
              if ( onError ) {

                onError({
                  type      : "TOKEN_IS_EXPIRED",
                  message   : translateCell("notifications.tokenIsExpired"),
                  source    : "WARNING",
                  detail    : _url + queries
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", translateCell("notifications.tokenIsExpired"), "error");
                ConsoleLog.double("🔴 API Path Details [usePostFetch]", _url + queries, "error");
                addNotificationError("notifications.tokenIsExpired");
              }

              logout("tokenIsExpired");
              return;

            }
            else if ( response.data.warnings[0].code === "adminBeApiTokenLimitExceeded" ) {
              
              if ( onError ) {

                onError({
                  type      : "TOKEN_LIMIT_EXCEEDED",
                  message   : translateCell("notifications.adminBeApiTokenLimitExceeded"),
                  source    : "WARNING",
                  detail    : _url + queries
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", translateCell("notifications.adminBeApiTokenLimitExceeded"), "error");
                ConsoleLog.double("🔴 API Path Details [usePostFetch]", _url + queries, "error");
                addNotificationError("notifications.adminBeApiTokenLimitExceeded");
              }
              
              logout("adminBeApiTokenLimitExceeded");
              return;

            }
            else if ( response.data.warnings[0].code === APIPredefinedCodeValues.INVALID ) {

              ConsoleLog.double("🟡 Inn.tech Web Warning [usePostFetch]", translateCell("notifications.invalid"), "warning");
              ConsoleLog.double("🟡 API Path Details [usePostFetch]", _url + queries, "warning");

            }

          }
          else if ( response.data.hasOwnProperty("error") ) {

            if ( onError ) {
              onError(response.data.error);
            }
            else {
              ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", response.data.error.type + ": " + response.data.error.detail, "error");
              ConsoleLog.double("🔴 API Path Details [usePostFetch]", _url + queries, "error");
              addNotificationError(response.data.error.type + ": " + response.data.error.detail);
            }
            
            return;

          }

        }
        
        onFetched(response.data);

      })
      .catch((error:any) => {

        let message = "";

        if ( error.hasOwnProperty("data") ) {
          if ( error.response.hasOwnProperty("response") ) {
            message = error.response.data.error.message;
          }
          else {
            message = error.response.statusText;
          }
        }

        if (axios.isCancel(error)) {
          onCanceled(error);
        }
        else {
          
          if (error.response) {

            if (error.response.status === 400) {
              if ( onError ) {

                onError({
                  type      : "BAD_REQUEST",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.badRequest") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "400: Bad Request", "error");
              }
            }
            else if (error.response.status === 401) {
              if ( onError ) {

                onError({
                  type      : "UNAUTHORIZED_USER",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.unauthorizedUser") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "401: Unauthorized User", "error");
              }
            }
            else if (error.response.status === 404) {
              if ( onError ) {

                onError({
                  type      : "RESOURCE_NOT_FOUND",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.resourceNotFound") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "404: Resource not found: - " + _url, "error");
              }
            } 
            else if (error.response.status === 408) {
              if ( onError ) {

                onError({
                  type      : "TIMEOUT",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.timeout") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "408: Timeout", "error");
              }
            } 
            else if (error.response.status === 413) {
              if ( onError ) {

                onError({
                  type      : "REQUEST_ENTITY_TOO_LARGE",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.requestEntityTooLarge") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "413: Request Entity Too Large", "error");
              }
            } 
            else if (error.response.status === 500) {
              if ( onError ) {

                onError({
                  type      : "INTERNAL_SERVER_ERROR",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.internalServerError") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "500: Internal Server Error", "error");
              }
              logout("internalServerError");
            } 
            else if (error.response.status === 502) { 
              if ( onError ) {

                onError({
                  type      : "BAD_GATEWAY",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.badGateway") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "502: Bad Gateway", "error");
              }
              logout("badGateway");
            } 
            else if (error.response.status === 503) {
              if ( onError ) {

                onError({
                  type      : "SERVICE_TEMPORARILY_UNAVAILABLE",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.serviceTemporarilyUnavailable") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "503: Service Temporarily Unavailable", "error");
              }
              logout("serviceTemporarilyUnavailable");
            } 
            else if (error.response.status === 504) {
              if ( onError ) {

                onError({
                  type      : "PROXY_ERROR",
                  message   : message,
                  source    : "ERROR",
                  detail    : translateCell("notifications.proxyError") + " - " + _url
                });
              }
              else {
                ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "504: Proxy Error", "error");
              }
            }

            ConsoleLog.double("🔴 API Path Details [usePostFetch]", _url + queries, "error");

          } 
          else if (error.request) {

            logout("noResponseFromApiPath");

            if ( onError ) {
              onError({
                type      : "NO_RESPONSE_FROM_API_PATH",
                message   : message,
                source    : "ERROR",
                detail    : translateCell("notifications.noResponseFromApiPath") + " - " + _url
              });
            }
            else {
              ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "No Response From API Path", "error");
              ConsoleLog.double("🔴 Error Request Details [usePostFetch]", error.request, "error");
            }

            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js

          } 
          else {

            // Something happened in setting up the request that triggered an Error
            console.log(error);
            
            if ( onError ) {
              onError(error);
            }
            else {
              ConsoleLog.double("🔴 Inn.tech Web Error [usePostFetch]", "Unhandled Error", "error");
              logout("unhandledError");
            }

          }

        }
      });

  }

  return { fetch, cancel };

};